import { projectFinal } from './projection';

/**
 * 給業務看的目標：距目標還差多少業績、要賣幾間、單價要多少。
 *
 * 拆法是「以間數為主、反推所需單價」，而不是「以現行單價反推間數」——
 * 前者會自己揭露可行性：把尚可售間夜放在一起看，就知道這個目標是量能追得回來、
 * 還是非漲價不可。後者永遠算得出一個數，但不會告訴你那個數做不做得到。
 */
export function targetSummary(items, { target }) {
  const days = items || [];
  let sellable = 0;      // 可售房晚 (容量)
  let otbNights = 0;     // 在手房晚
  let otbRevenue = 0;    // 在手房租
  let needNights = 0;    // 逐日「目標房晚 − 在手房晚」，只計不足者
  let naturalNights = 0; // 依歷史節奏預估會自然進的房晚 (P50)

  days.forEach((it) => {
    const cap = it.cap || 0;
    if (!cap) return;
    sellable += cap;
    otbNights += it.otb_rooms || 0;
    otbRevenue += (it.otb_rooms || 0) * (it.adr || 0);
    // 逐日先算好當天的需求，自然進帳要用同一天的需求夾住 —— 不能整段才夾。
    const needDay = Math.max(0, (target / 100) * cap - (it.otb_rooms || 0));
    needNights += needDay;
    const band = projectFinal(it);
    if (band) {
      // 自然進帳逐日夾在 [0, 當天需求] 內：已超標日的自然進帳補不到偏弱日的缺口，
      // 不能拿來抵銷；下界的 0 同時擋住預測帶往下修正時可能出現的負值。
      const naturalDay = ((band.mid - it.occ_now) / 100) * cap;
      naturalNights += Math.max(0, Math.min(naturalDay, needDay));
    }
  });

  if (!sellable) return null;

  const adr = otbNights ? otbRevenue / otbNights : 0;
  const targetRevenue = Math.round((target / 100) * adr * sellable);
  const gap = Math.round(targetRevenue - otbRevenue);
  const need = Math.round(needNights);
  const natural = Math.round(naturalNights);
  const unsold = Math.max(0, sellable - otbNights);

  return {
    targetRevenue,
    otbRevenue: Math.round(otbRevenue),
    otbNights,
    gap,
    needNights: need,
    // 沒有任何在手訂房時 adr 退回 0，gap 與所需均價會一路算成 0 ——
    // 「平均單價需達 NT$0」不是目標而是除法的副產物，這種情況要說「不知道」而非給業務一個 0
    //
    // gap <= 0 代表營收目標已達成 (或有日期超賣把整段拉過目標)，此時不存在「還需要賣多少價」。
    // needNights 是逐日夾非負後加總、gap 卻是整段聚合，兩者基準不同，
    // 超賣日會讓 gap 轉負而 need 仍為正，硬除會得到負的單價目標。
    needAdr: need > 0 && otbNights && gap > 0 ? Math.round(gap / need) : null,
    naturalNights: natural,
    fightNights: Math.max(0, need - natural),
    sellableNights: sellable,
    coverage: unsold ? Math.round((need / unsold) * 100) : null,
  };
}
